import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Share2, Check, RotateCcw, Heart, Calendar, MessageCircle, Gift } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { QUIZZES } from '../data/quizzes';
import { trackPageView, trackDownloadClick } from '../lib/track';

const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';
const LOGO_URL = '/couplein_logo.png';
const SHOTS = {
  mood: '/shots/mood_sync.png',
  brownies: '/shots/brownie_points.png',
  calendar: '/shots/calendar_reminders.png',
  conflict: '/conflict_real_faces.png',
};

const StoreButtons = ({ className = '', trackId }) => (
  <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
    <a href={IOS_LINK} target="_blank" rel="noopener noreferrer" data-testid="quiz-ios-download-btn"
      onClick={() => trackId && trackDownloadClick(trackId)}
      className="flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 px-6 py-3.5">
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Download on the</p>
        <p className="font-semibold text-base -mt-0.5">App Store</p>
      </div>
    </a>
    <a href={ANDROID_LINK} target="_blank" rel="noopener noreferrer" data-testid="quiz-android-download-btn"
      onClick={() => trackId && trackDownloadClick(trackId)}
      className="flex items-center justify-center gap-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 px-6 py-3.5">
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
      </svg>
      <div className="text-left">
        <p className="text-[10px] opacity-80">Get it on</p>
        <p className="font-semibold text-base -mt-0.5">Google Play</p>
      </div>
    </a>
  </div>
);

import Navbar from '../components/Navbar';

const CTA_FEATURES = [
  { icon: Heart, shot: SHOTS.mood, title: '60-Second Check-in', text: 'Sync moods daily. Catch bad days before they become bad weeks.', to: '/stay-connected-in-60-seconds' },
  { icon: Gift, shot: SHOTS.brownies, title: 'Brownie Points', text: 'Turn everyday kindness into a game you both win. Every gesture counts.', to: '/love-with-intentionality' },
  { icon: Calendar, shot: SHOTS.calendar, title: 'Shared Calendar', text: 'Never forget an anniversary, date night, or "you-didn\'t-tell-me" again.', to: '/never-forget-anything' },
  { icon: MessageCircle, shot: SHOTS.conflict, title: 'Guided Mode', text: 'A 5-minute fair-turn conversation that ends fights instead of fueling them.', to: '/solve-fights-in-5-minutes' },
];

const ResultCTA = ({ result, quiz }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5, duration: 0.6 }}
    className="mt-10 lg:-mx-56 rounded-3xl overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-pink-950 text-white shadow-2xl"
    data-testid="quiz-cta-section"
  >
    {/* Hard-sell challenge header */}
    <div className="text-center px-6 pt-12 pb-8 sm:px-12">
      <motion.p
        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.7 }}
        className="inline-block bg-pink-500/15 border border-pink-500/40 text-pink-300 text-xs sm:text-sm font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6"
      >
        ⚡ The CoupleIn 7-Day Challenge
      </motion.p>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-5" data-testid="quiz-cta-headline">
        Try CoupleIn for 7 days.
        <br />
        <span className="bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent">
          If your relationship doesn't improve, uninstall it.
        </span>
      </h2>
      <p className="text-gray-300 max-w-xl mx-auto text-base sm:text-lg mb-2">
        That's the deal. No card. No excuses. Just 7 days.
      </p>
      <p className="text-gray-400 max-w-xl mx-auto text-sm mb-8">{result.cta}</p>
      <div className="flex justify-center">
        <StoreButtons trackId={`quiz-${quiz.id}`} />
      </div>
      <p className="text-xs text-gray-500 mt-4">Free to start · 60-second setup · Uninstalling takes 3 seconds, but you won't.</p>
    </div>

    {/* Features + screenshots */}
    <div className="border-t border-white/10 px-6 py-10 sm:px-12">
      <p className="text-center text-sm uppercase tracking-widest text-gray-500 mb-8">What you get from day one</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {CTA_FEATURES.map(({ icon: Icon, shot, title, text, to }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 + i * 0.1 }}
            className="text-center"
          >
            <Link to={to} data-testid={`quiz-cta-feature-${i}`} className="block group">
              <div className="relative mx-auto w-full max-w-[150px] rounded-2xl overflow-hidden border-2 border-white/10 shadow-xl mb-4 group-hover:scale-105 group-hover:border-pink-500/50 transition-all">
                <img src={shot} alt={`CoupleIn ${title}`} loading="lazy" className="block w-full aspect-[9/16] object-cover object-top" />
              </div>
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Icon className="w-4 h-4 text-pink-400" />
                <p className="font-bold text-sm group-hover:text-pink-300 transition-colors">{title}</p>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">{text}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>

    {/* Closing punch */}
    <div className="border-t border-white/10 text-center px-6 py-10 sm:px-12 bg-black/30">
      <p className="text-xl sm:text-2xl font-bold mb-1">7 days. That's all we ask.</p>
      <p className="text-gray-400 text-sm mb-6">Worst case, you delete an app. Best case, you change your relationship.</p>
      <div className="flex justify-center">
        <StoreButtons trackId={`quiz-${quiz.id}`} />
      </div>
    </div>
  </motion.div>
);

export default function Quiz() {
  const { quizId } = useParams();
  const quiz = QUIZZES[quizId];
  const [step, setStep] = useState(-1); // -1 intro, 0..n-1 questions, n result
  const [scores, setScores] = useState({});
  const [copied, setCopied] = useState(false);

  const ranked = useMemo(() => {
    if (!quiz) return [];
    return Object.keys(quiz.results).sort((a, b) => (scores[b] || 0) - (scores[a] || 0));
  }, [scores, quiz]);

  useEffect(() => { if (quiz) trackPageView(`quiz-${quiz.id}`); }, [quiz]);

  if (!quiz) return <Navigate to="/" replace />;

  const total = quiz.questions.length;
  const isResult = step >= total;
  const resultKey = ranked[0];
  const result = quiz.results[resultKey];
  const secondaryKey = quiz.showSecondary && (scores[ranked[1]] || 0) > 0 ? ranked[1] : null;
  const secondary = secondaryKey ? quiz.results[secondaryKey] : null;

  const answer = (key) => {
    setScores((s) => ({ ...s, [key]: (s[key] || 0) + 1 }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const restart = () => { setScores({}); setStep(-1); setCopied(false); };

  const share = async () => {
    const url = window.location.href;
    const text = secondary
      ? `I just found out my top 2 love languages: "${result.title}" ${result.emoji} and "${secondary.title}" ${secondary.emoji}. Take the free quiz and find yours!`
      : `I just found out my ${quiz.title.replace(' Quiz', '')} is "${result.title}" ${result.emoji}, take the free quiz and find yours!`;
    if (navigator.share) {
      try { await navigator.share({ title: quiz.title, text, url }); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      toast.success('Link copied, send it to your partner!');
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-purple-50">
      <SEO
        title={quiz.seo.title}
        description={quiz.seo.description}
        keywords={quiz.seo.keywords}
        path={`/quiz/${quiz.id}`}
      />
      <Navbar />

      <main className="max-w-2xl mx-auto px-4 pt-28 pb-20">
        <AnimatePresence mode="wait">
          {step === -1 && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="text-center" data-testid="quiz-intro">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.1 }}
                className="text-7xl mb-6">{quiz.emoji}</motion.div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">{quiz.title}</h1>
              <p className={`text-lg font-medium bg-gradient-to-r ${quiz.color} bg-clip-text text-transparent mb-4`}>{quiz.tagline}</p>
              <p className="text-gray-600 max-w-md mx-auto mb-10">{quiz.intro}</p>
              <button onClick={() => setStep(0)} data-testid="quiz-start-btn"
                className={`inline-flex items-center gap-2 bg-gradient-to-r ${quiz.color} text-white font-semibold text-lg px-10 py-4 rounded-full shadow-lg shadow-pink-200 hover:scale-105 active:scale-95 transition-transform`}>
                Start the quiz <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-xs text-gray-400 mt-4">Takes about 2 minutes · 100% free</p>
            </motion.div>
          )}

          {step >= 0 && !isResult && (
            <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25 }} data-testid="quiz-question">
              <div className="mb-8">
                <div className="flex justify-between text-xs text-gray-500 mb-2">
                  <span>Question {step + 1} of {total}</span>
                  <span>{Math.round((step / total) * 100)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-testid="quiz-progress">
                  <motion.div className={`h-full bg-gradient-to-r ${quiz.color} rounded-full`}
                    animate={{ width: `${(step / total) * 100}%` }} transition={{ duration: 0.4 }} />
                </div>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-8">{quiz.questions[step].q}</h2>
              <div className="space-y-3">
                {quiz.questions[step].options.map((opt, i) => (
                  <motion.button key={opt.text} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }} onClick={() => answer(opt.key)}
                    data-testid={`quiz-option-${i}`}
                    className="w-full text-left bg-white border border-gray-200 rounded-2xl px-5 py-4 text-gray-700 hover:border-pink-400 hover:bg-pink-50/50 hover:shadow-md active:scale-[0.98] transition-all">
                    {opt.text}
                  </motion.button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep((s) => s - 1)} className="mt-6 text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1">
                  <ArrowLeft className="w-4 h-4" /> Previous question
                </button>
              )}
            </motion.div>
          )}

          {isResult && (
            <motion.div key="result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} data-testid="quiz-result">
              <div className="text-center">
                <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.15 }} className="text-8xl mb-4">
                  {result.emoji}
                </motion.div>
                <p className="text-sm uppercase tracking-widest text-gray-400 mb-2">{secondary ? 'Your primary love language' : 'Your result'}</p>
                <h1 className={`text-4xl sm:text-5xl font-bold bg-gradient-to-r ${quiz.color} bg-clip-text text-transparent mb-3`} data-testid="quiz-result-title">
                  {result.title}
                </h1>
                <p className="text-xl font-semibold text-gray-800 mb-4">{result.headline}</p>
                <p className="text-gray-600 max-w-lg mx-auto mb-8">{result.description}</p>

                {secondary && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                    className="max-w-lg mx-auto mb-8 bg-white rounded-2xl border-2 border-pink-100 shadow-sm p-6 text-left"
                    data-testid="quiz-secondary-result"
                  >
                    <p className="text-xs uppercase tracking-widest text-gray-400 mb-3">🥈 Your secondary love language</p>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">{secondary.emoji}</span>
                      <div>
                        <p className={`text-xl font-bold bg-gradient-to-r ${quiz.color} bg-clip-text text-transparent`} data-testid="quiz-secondary-title">{secondary.title}</p>
                        <p className="text-sm font-semibold text-gray-700">{secondary.headline}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{secondary.description}</p>
                    <p className="text-xs text-gray-400 mt-3">Most people speak a blend of two languages. When your primary need is met, this one is what fills the tank the rest of the way.</p>
                  </motion.div>
                )}

                {result.deepDive && (
                  <div className="text-left max-w-lg mx-auto space-y-5 mb-8" data-testid="quiz-deep-dive">
                    {result.deepDive.map(({ heading, text }) => (
                      <div key={heading} className="bg-white/70 rounded-2xl border border-gray-100 p-5">
                        <p className={`font-bold text-sm uppercase tracking-wide bg-gradient-to-r ${quiz.color} bg-clip-text text-transparent mb-2`}>{heading}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left max-w-lg mx-auto mb-8">
                  <p className="font-semibold text-gray-900 mb-3">💡 What to do with this</p>
                  <ul className="space-y-2.5">
                    {result.tips.map((tip) => (
                      <li key={tip} className="flex gap-2.5 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-pink-500 flex-shrink-0 mt-0.5" /> {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button onClick={share} data-testid="quiz-share-btn"
                    className={`inline-flex items-center gap-2 bg-gradient-to-r ${quiz.color} text-white font-semibold px-8 py-3.5 rounded-full shadow-lg hover:scale-105 active:scale-95 transition-transform`}>
                    {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
                    {copied ? 'Copied!' : 'Share with your partner'}
                  </button>
                  <button onClick={restart} data-testid="quiz-retake-btn"
                    className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 font-medium px-6 py-3.5 rounded-full border border-gray-200 hover:border-gray-300 transition-colors">
                    <RotateCcw className="w-4 h-4" /> Retake quiz
                  </button>
                </div>

                {quiz.partnerNote && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className="mt-10 max-w-lg mx-auto text-left bg-amber-50 border-2 border-amber-200 rounded-2xl p-6"
                    data-testid="quiz-partner-note"
                  >
                    <p className="font-bold text-gray-900 mb-2">⚠️ {quiz.partnerNote.title}</p>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4">{quiz.partnerNote.text}</p>
                    <button onClick={share} data-testid="quiz-partner-share-btn"
                      className="w-full inline-flex items-center justify-center gap-2 bg-gray-900 text-white font-semibold px-6 py-3 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all">
                      <Share2 className="w-4 h-4" /> {quiz.partnerNote.cta}
                    </button>
                  </motion.div>
                )}

                {quiz.appPitch && (
                  <p className="mt-10 max-w-lg mx-auto text-gray-700 text-base leading-relaxed" data-testid="quiz-app-pitch">
                    {quiz.appPitch.split('**').map((part, i) =>
                      i % 2 === 1 ? <strong key={i} className="font-bold text-gray-900">{part}</strong> : part
                    )}
                  </p>
                )}
              </div>

              <ResultCTA result={result} quiz={quiz} />

              <div className="text-center mt-10">
                <p className="text-sm text-gray-500">
                  Curious about the other quiz?{' '}
                  <Link
                    to={`/quiz/${quiz.id === 'love-language' ? 'attachment-style' : 'love-language'}`}
                    onClick={restart}
                    className="text-pink-500 font-semibold hover:underline"
                    data-testid="quiz-other-link"
                  >
                    Take the {quiz.id === 'love-language' ? 'Attachment Style' : 'Love Language'} Quiz →
                  </Link>
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
